import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { Plus, Search, MoreHorizontal, Eye, Trash, User, Building2, Mail, Phone, Linkedin, ArrowUpDown, ChevronDown, ChevronRight, BarChart3, List, Heart, Clock, X, Filter } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import ContactDialog from '../components/ContactDialog';
import ExportMenu from '../components/ExportMenu';
import ClientTypeFilter from '../components/ClientTypeFilter';
import { matchesClientTypeFilter } from '../utils/clientType';
import { useUser } from '../context/UserContext';
import { ContactsAnalytics } from '../components/analytics';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FREQUENCY_OPTIONS = [
  { value: 'estrategico', label: 'Estratégico — 15 días' },
  { value: 'prioritario', label: 'Prioritario — 30 días' },
  { value: 'activo', label: 'Activo — 60 días' },
  { value: 'orbita', label: 'En órbita — 90 días' },
  { value: 'no_seguir', label: 'No seguir' },
];

const Contacts = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isEditor, currentUser, editorUsers } = useUser();
  const [contacts, setContacts] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [typsaOrgUnits, setTypsaOrgUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('owner');
  const [expandedGroups, setExpandedGroups] = useState({});
  const [expandedOrgCategories, setExpandedOrgCategories] = useState({});
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [viewMode, setViewMode] = useState('list');
  
  // Client type filter state
  const [clientTypeFilter, setClientTypeFilter] = useState(['typsa_gg', 'typsa_colab', 'privada', 'publica']);

  // Advanced filters (Phase 2 - jumped from analytics or set manually)
  // All multi-select via comma-separated URL values
  const parseCsv = (v) => (v ? v.split(',').map(s => s.trim()).filter(Boolean) : []);
  const [complianceFilter, setComplianceFilter] = useState(parseCsv(searchParams.get('compliance')));
  const [cadenceFilter, setCadenceFilter] = useState(parseCsv(searchParams.get('cadence')));
  const [recencyFilter, setRecencyFilter] = useState(parseCsv(searchParams.get('recency')));
  const [seniorityFilter, setSeniorityFilter] = useState(parseCsv(searchParams.get('seniority')));
  const [ownerAdvFilter, setOwnerAdvFilter] = useState(parseCsv(searchParams.get('owner')));

  // Sync URL when filters change (bidirectional)
  useEffect(() => {
    const params = new URLSearchParams(searchParams);
    const setOrDelete = (key, arr) => {
      if (arr && arr.length > 0) params.set(key, arr.join(','));
      else params.delete(key);
    };
    setOrDelete('compliance', complianceFilter);
    setOrDelete('cadence', cadenceFilter);
    setOrDelete('recency', recencyFilter);
    setOrDelete('seniority', seniorityFilter);
    setOrDelete('owner', ownerAdvFilter);
    setSearchParams(params, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [complianceFilter, cadenceFilter, recencyFilter, seniorityFilter, ownerAdvFilter]);

  // Also react to browser back/forward: re-read from URL when it changes externally
  useEffect(() => {
    setComplianceFilter(parseCsv(searchParams.get('compliance')));
    setCadenceFilter(parseCsv(searchParams.get('cadence')));
    setRecencyFilter(parseCsv(searchParams.get('recency')));
    setSeniorityFilter(parseCsv(searchParams.get('seniority')));
    setOwnerAdvFilter(parseCsv(searchParams.get('owner')));
    // If any advanced filter arrives in URL, switch to List view so the user sees the filtered list
    const advParams = ['compliance', 'cadence', 'recency', 'seniority', 'owner'];
    if (advParams.some(k => searchParams.get(k))) {
      setViewMode('list');
    } else if (searchParams.get('view') === 'analytics') {
      setViewMode('analytics');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  useEffect(() => {
    fetchData();
    fetchTypsaOrgUnits();
  }, []);

  // Reset expanded groups when sort changes
  useEffect(() => {
    setExpandedGroups({});
  }, [sortBy]);

  const toggleGroup = (key) => {
    setExpandedGroups(prev => {
      // If not toggled yet, take the current default (based on sortBy) as the starting point
      // so the first click actually flips it visibly.
      const alphabeticalDefault = sortBy === 'last_name';
      const currentDefault = alphabeticalDefault;
      const currentValue = Object.prototype.hasOwnProperty.call(prev, key) ? prev[key] : currentDefault;
      return { ...prev, [key]: !currentValue };
    });
  };

  const fetchData = async () => {
    try {
      const [contactsRes, companiesRes] = await Promise.all([
        axios.get(`${API}/contacts?include_org_unit=true`),
        axios.get(`${API}/companies`)
      ]);
      setContacts(contactsRes.data);
      setCompanies(companiesRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTypsaOrgUnits = async () => {
    try {
      const response = await axios.get(`${API}/typsa-org-units`);
      setTypsaOrgUnits(response.data);
    } catch (error) {
      console.error('Error fetching TYPSA org units:', error);
    }
  };

  const toggleOrgCategory = (key) => {
    setExpandedOrgCategories(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const getCompanyName = (companyId) => {
    if (!companyId) return 'Sin empresa';
    const company = companies.find(c => c.id === companyId);
    return company ? company.name : 'Empresa no encontrada';
  };

  const getFullName = (contact) => {
    if (contact.first_name || contact.last_name) {
      return `${contact.first_name || ''} ${contact.last_name || ''}`.trim();
    }
    return contact.name || 'Sin nombre';
  };

  const getLastName = (contact) => {
    if (contact.last_name) return contact.last_name;
    if (contact.name) {
      const parts = contact.name.split(' ');
      return parts.length > 1 ? parts.slice(1).join(' ') : parts[0];
    }
    return '';
  };

  const getTypsaCategoryLabel = (category) => {
    const labels = {
      'division': 'División',
      'departamento': 'Departamento',
      'territorio': 'Territorio',
      'grupo': 'Grupo'
    };
    return labels[category] || category;
  };

  // Toggle client type filter
  const toggleClientTypeFilter = (type) => {
    setClientTypeFilter(prev => {
      if (prev.includes(type)) {
        return prev.filter(t => t !== type);
      } else {
        return [...prev, type];
      }
    });
  };

  // Determine contact's client type
  const getContactClientType = (contact) => {
    if (contact.typsa_org_unit_id) {
      return 'typsa';
    }
    const company = companies.find(c => c.id === contact.company_id);
    if (company) {
      if (company.company_type === 'typsa' || company.name?.toUpperCase().includes('TYPSA')) {
        return 'typsa';
      }
      return company.company_type; // 'privada' or 'publica'
    }
    return 'privada'; // Default
  };

  const handleView = (contact) => {
    navigate(`/contacts/${contact.id}`);
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    
    setDeleting(true);
    try {
      await axios.delete(`${API}/contacts/${deleteConfirm.id}`);
      toast.success('Contacto eliminado');
      fetchData();
    } catch (error) {
      console.error('Error deleting contact:', error);
      toast.error('Error al eliminar el contacto');
    } finally {
      setDeleting(false);
      setDeleteConfirm(null);
    }
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setEditingContact(null);
  };

  const handleDialogSuccess = () => {
    fetchData();
    handleDialogClose();
  };

  // ─── Advanced filter helpers ───
  // Compliance/cadence/recency/seniority are computed SERVER-SIDE now (contact.compliance).
  // These local helpers exist only for the `recency` bucket derivation used in filtering.
  const complianceOf = (c) => c.compliance || 'sin_compromiso';
  const recencyBucket = (c) => {
    // Use days_since_last_contact (real interaction) — do NOT fall back to created_at here,
    // recency measures interaction. If no last contact ever, bucket is 'nunca'.
    if (!c.last_contact_at) return 'nunca';
    const d = c.days_since_last_contact;
    if (d == null) return 'nunca';
    if (d <= 30) return '0-30';
    if (d <= 90) return '31-90';
    if (d <= 180) return '91-180';
    if (d <= 365) return '181-365';
    return 'gt-365';
  };
  const SENIORITY_KEYWORDS = {
    direccion: ['director', 'directora', 'presidente', 'presidenta', 'ceo', 'cfo', 'coo', 'cto', 'cio', 'gerente', 'gerenta', 'vp', 'vicepresidente', 'vicepresidenta', 'socio', 'socia', 'founder', 'fundador', 'fundadora', 'consejero', 'consejera', 'owner', 'propietario', 'propietaria'],
    mando_intermedio: ['jefe', 'jefa', 'responsable', 'supervisor', 'supervisora', 'coordinador', 'coordinadora', 'lead', 'líder', 'lider', 'head', 'manager'],
    tecnico: ['técnico', 'tecnico', 'técnica', 'tecnica', 'ingeniero', 'ingeniera', 'engineer', 'arquitecto', 'arquitecta', 'architect', 'analista', 'analyst', 'consultor', 'consultora', 'consultant', 'developer', 'desarrollador', 'programador', 'especialista'],
  };
  const seniorityBucket = (role) => {
    if (!role || !role.trim()) return 'sin_cargo';
    const r = role.toLowerCase();
    for (const [b, kws] of Object.entries(SENIORITY_KEYWORDS)) {
      if (kws.some(kw => r.includes(kw))) return b;
    }
    return 'otros';
  };
  const CADENCE_LIMITS = { estrategico: 15, prioritario: 30, activo: 60, orbita: 90 };

  // Human-readable labels for filter chips and dropdowns
  const COMPLIANCE_LABELS = {
    al_dia: 'Al día',
    fuera_de_plazo: 'Fuera de plazo',
    sin_fecha: 'Sin fecha registrada',
    excluido: 'Excluido',
    sin_compromiso: 'Sin compromiso',
  };
  const CADENCE_LABELS_MAP = {
    estrategico: 'Estratégico',
    prioritario: 'Prioritario',
    activo: 'Activo',
    orbita: 'Órbita',
    no_seguir: 'No seguir',
    sin_definir: 'Sin cadencia definida',
  };
  const RECENCY_LABELS = {
    '0-30': '0-30 días', '31-90': '31-90 días', '91-180': '91-180 días',
    '181-365': '181-365 días', 'gt-365': 'Más de 365 días', 'nunca': 'Nunca contactado',
  };
  const SENIORITY_LABELS_MAP = {
    direccion: 'Dirección', mando_intermedio: 'Mando intermedio', tecnico: 'Técnico',
    otros: 'Otros', sin_cargo: 'Sin cargo',
  };
  const humanLabel = (kind, val) => {
    const map = { compliance: COMPLIANCE_LABELS, cadence: CADENCE_LABELS_MAP, recency: RECENCY_LABELS, seniority: SENIORITY_LABELS_MAP };
    return map[kind]?.[val] || val;
  };

  const anyAdvFilterActive = complianceFilter.length + cadenceFilter.length + recencyFilter.length + seniorityFilter.length + ownerAdvFilter.length > 0;

  // Filter contacts by search and client type
  const filteredContacts = useMemo(() => {
    return contacts.filter(contact => {
      const clientType = getContactClientType(contact);
      if (!matchesClientTypeFilter(clientType, clientTypeFilter)) return false;
      if (complianceFilter.length > 0 && !complianceFilter.includes(complianceOf(contact))) return false;
      if (cadenceFilter.length > 0) {
        const bucket = contact.cultivation_frequency || 'sin_definir';
        if (!cadenceFilter.includes(bucket)) return false;
      }
      if (recencyFilter.length > 0 && !recencyFilter.includes(recencyBucket(contact))) return false;
      if (seniorityFilter.length > 0 && !seniorityFilter.includes(seniorityBucket(contact.role))) return false;
      if (ownerAdvFilter.length > 0) {
        const own = (contact.owner || '').trim();
        const wants = ownerAdvFilter.map(o => o === '__unassigned__' ? '' : o);
        if (!wants.some(w => w === own)) return false;
      }
      const searchLower = search.toLowerCase();
      const fullName = getFullName(contact).toLowerCase();
      return (
        fullName.includes(searchLower) ||
        contact.email?.toLowerCase().includes(searchLower) ||
        contact.role?.toLowerCase().includes(searchLower) ||
        contact.owner?.toLowerCase().includes(searchLower) ||
        getCompanyName(contact.company_id).toLowerCase().includes(searchLower)
      );
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contacts, search, clientTypeFilter, companies, complianceFilter, cadenceFilter, recencyFilter, seniorityFilter, ownerAdvFilter]);

  const clearAllAdvFilters = () => {
    setComplianceFilter([]);
    setCadenceFilter([]);
    setRecencyFilter([]);
    setSeniorityFilter([]);
    setOwnerAdvFilter([]);
  };

  const toggleInSet = (setter, arr, val) => {
    if (arr.includes(val)) setter(arr.filter(x => x !== val));
    else setter([...arr, val]);
  };

  // Sort and group contacts
  const getGroupedContacts = () => {
    let sorted = [...filteredContacts];
    
    if (sortBy === 'last_name') {
      sorted.sort((a, b) => {
        const lastNameA = getLastName(a).toLowerCase();
        const lastNameB = getLastName(b).toLowerCase();
        return lastNameA.localeCompare(lastNameB, 'es');
      });
      
      // Group by first letter of last name
      const groups = {};
      sorted.forEach(contact => {
        const lastName = getLastName(contact);
        const letter = lastName ? lastName[0].toUpperCase() : '#';
        if (!groups[letter]) groups[letter] = [];
        groups[letter].push(contact);
      });
      
      // Sort group keys alphabetically
      const sortedKeys = Object.keys(groups).sort((a, b) => a.localeCompare(b, 'es'));
      return sortedKeys.map(key => ({ key, label: key, contacts: groups[key] }));
      
    } else if (sortBy === 'owner') {
      sorted.sort((a, b) => {
        const ownerA = (a.owner || 'Sin responsable').toLowerCase();
        const ownerB = (b.owner || 'Sin responsable').toLowerCase();
        return ownerA.localeCompare(ownerB, 'es');
      });
      
      // Group by owner with breakdown by company type
      const groups = {};
      sorted.forEach(contact => {
        const owner = contact.owner || 'Sin responsable';
        if (!groups[owner]) {
          groups[owner] = {
            contacts: [],
            breakdown: {
              typsa: { total: 0, division: 0, departamento: 0, territorio: 0, grupo: 0 },
              publica: 0,
              privada: 0
            }
          };
        }
        groups[owner].contacts.push(contact);
        
        // Calculate breakdown
        const company = companies.find(c => c.id === contact.company_id);
        const orgUnit = contact.typsa_org_unit_id 
          ? typsaOrgUnits.find(u => u.id === contact.typsa_org_unit_id)
          : null;
        
        if (orgUnit) {
          // Contact linked to TYPSA org unit
          groups[owner].breakdown.typsa.total++;
          if (orgUnit.category === 'division') groups[owner].breakdown.typsa.division++;
          else if (orgUnit.category === 'departamento') groups[owner].breakdown.typsa.departamento++;
          else if (orgUnit.category === 'territorio') groups[owner].breakdown.typsa.territorio++;
          else if (orgUnit.category === 'grupo') groups[owner].breakdown.typsa.grupo++;
        } else if (company) {
          if (company.company_type === 'publica') {
            groups[owner].breakdown.publica++;
          } else if (company.company_type === 'privada') {
            groups[owner].breakdown.privada++;
          } else if (company.company_type === 'typsa' || company.name?.toUpperCase().includes('TYPSA')) {
            groups[owner].breakdown.typsa.total++;
          } else {
            // Default to privada for legacy
            groups[owner].breakdown.privada++;
          }
        }
      });
      
      // Sort group keys alphabetically
      const sortedKeys = Object.keys(groups).sort((a, b) => a.localeCompare(b, 'es'));
      return sortedKeys.map(key => ({ 
        key, 
        label: key, 
        contacts: groups[key].contacts,
        breakdown: groups[key].breakdown
      }));
      
    } else if (sortBy === 'company_id') {
      sorted.sort((a, b) => {
        const companyA = getCompanyName(a.company_id).toLowerCase();
        const companyB = getCompanyName(b.company_id).toLowerCase();
        return companyA.localeCompare(companyB, 'es');
      });
      
      // Group by company
      const groups = {};
      sorted.forEach(contact => {
        const companyName = getCompanyName(contact.company_id);
        if (!groups[companyName]) {
          groups[companyName] = {
            contacts: [],
            isTypsa: companyName.toUpperCase().includes('TYPSA'),
            subgroups: {}
          };
        }
        
        // For TYPSA, group by organization unit
        if (groups[companyName].isTypsa && contact.typsa_org_unit_id) {
          const orgUnit = typsaOrgUnits.find(u => u.id === contact.typsa_org_unit_id);
          if (orgUnit) {
            const subKey = `${orgUnit.category}-${orgUnit.value}`;
            if (!groups[companyName].subgroups[subKey]) {
              groups[companyName].subgroups[subKey] = {
                category: orgUnit.category,
                value: orgUnit.value,
                owner: orgUnit.owner,
                contacts: []
              };
            }
            groups[companyName].subgroups[subKey].contacts.push(contact);
          } else {
            // Org unit not found, put in ungrouped
            groups[companyName].contacts.push(contact);
          }
        } else if (groups[companyName].isTypsa && contact.typsa_category && contact.typsa_category_value) {
          // Legacy: use typsa_category fields
          const subKey = `${contact.typsa_category}-${contact.typsa_category_value}`;
          if (!groups[companyName].subgroups[subKey]) {
            groups[companyName].subgroups[subKey] = {
              category: contact.typsa_category,
              value: contact.typsa_category_value,
              contacts: []
            };
          }
          groups[companyName].subgroups[subKey].contacts.push(contact);
        } else {
          groups[companyName].contacts.push(contact);
        }
      });
      
      // Sort group keys alphabetically
      const sortedKeys = Object.keys(groups).sort((a, b) => a.localeCompare(b, 'es'));
      return sortedKeys.map(key => ({ 
        key, 
        label: key, 
        contacts: groups[key].contacts,
        isTypsa: groups[key].isTypsa,
        subgroups: groups[key].subgroups
      }));
    }
    
    return [{ key: 'all', label: '', contacts: sorted }];
  };

  const groupedContacts = getGroupedContacts();

  // Contact Card Component
  // Get company type info for a contact
  const getContactCompanyInfo = (contact) => {
    // Check if contact is linked to a TYPSA org unit
    if (contact.typsa_org_unit_id) {
      const orgUnit = typsaOrgUnits.find(u => u.id === contact.typsa_org_unit_id);
      if (orgUnit) {
        return {
          type: 'typsa',
          typeLabel: 'TYPSA',
          typeColor: 'bg-red-100 text-red-800 border-red-300',
          category: orgUnit.category,
          categoryLabel: orgUnit.category === 'division' ? 'División' 
            : orgUnit.category === 'departamento' ? 'Departamento'
            : orgUnit.category === 'territorio' ? 'Territorio'
            : orgUnit.category === 'grupo' ? 'Grupo' : null
        };
      }
    }
    
    // Check company type
    const company = companies.find(c => c.id === contact.company_id);
    if (company) {
      if (company.company_type === 'typsa' || company.name?.toUpperCase().includes('TYPSA')) {
        return {
          type: 'typsa',
          typeLabel: 'TYPSA',
          typeColor: 'bg-red-100 text-red-800 border-red-300',
          category: null,
          categoryLabel: null
        };
      } else if (company.company_type === 'publica') {
        return {
          type: 'publica',
          typeLabel: 'Pública',
          typeColor: 'bg-green-100 text-green-800 border-green-300',
          category: null,
          categoryLabel: null
        };
      } else {
        // privada or legacy (no type)
        return {
          type: 'privada',
          typeLabel: 'Privada',
          typeColor: 'bg-purple-100 text-purple-800 border-purple-300',
          category: null,
          categoryLabel: null
        };
      }
    }
    
    return null;
  };

  const handleFrequencyChange = async (contactId, value) => {
    if (!isEditor) return;
    const newValue = value === '__none__' ? null : value;
    try {
      await axios.patch(
        `${API}/contacts/${contactId}`,
        { cultivation_frequency: newValue },
        { headers: { 'X-User-Email': currentUser?.email } }
      );
      setContacts((prev) => prev.map((c) => (c.id === contactId ? { ...c, cultivation_frequency: newValue } : c)));
      toast.success('Frecuencia actualizada');
    } catch (error) {
      console.error('Error updating frequency:', error);
      toast.error('Error al actualizar la frecuencia');
    }
  };

  const handleOwnerChange = async (contactId, value) => {
    if (!isEditor) return;
    const newValue = value === '__none__' ? null : value;
    try {
      await axios.patch(
        `${API}/contacts/${contactId}`,
        { owner: newValue },
        { headers: { 'X-User-Email': currentUser?.email } }
      );
      setContacts((prev) => prev.map((c) => (c.id === contactId ? { ...c, owner: newValue } : c)));
      toast.success('Responsable actualizado');
    } catch (error) {
      console.error('Error updating owner:', error);
      toast.error('Error al actualizar el responsable');
    }
  };

  const ContactCard = ({ contact }) => {
    const companyInfo = getContactCompanyInfo(contact);
    
    return (
      <Card 
        data-testid={`contact-card-${contact.id}`} 
        className="relative cursor-pointer hover:shadow-md transition-shadow"
        onClick={() => navigate(`/contacts/${contact.id}`)}
      >
        <CardContent className="p-4">
          {/* Three-dot menu */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
              <Button
                variant="ghost"
                size="icon"
                className="absolute top-2 right-2 h-7 w-7"
                data-testid={`contact-menu-${contact.id}`}
              >
                <MoreHorizontal className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-40">
              <DropdownMenuItem 
                onClick={(e) => { e.stopPropagation(); handleView(contact); }}
                data-testid={`view-contact-${contact.id}`}
              >
                <Eye className="w-4 h-4 mr-2" />
                Ver
              </DropdownMenuItem>
              {isEditor && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem 
                    onClick={(e) => { e.stopPropagation(); setDeleteConfirm(contact); }}
                    className="text-destructive focus:text-destructive"
                    data-testid={`delete-contact-${contact.id}`}
                  >
                    <Trash className="w-4 h-4 mr-2" />
                    Eliminar
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>

          <div className="flex items-start gap-3 pr-8">
            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
              <User className="w-5 h-5 text-primary" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-medium text-sm truncate">{getFullName(contact)}</div>
              {contact.role && (
                <div className="text-xs text-muted-foreground truncate">{contact.role}</div>
              )}
              {/* Company type badges */}
              {companyInfo && (
                <div className="flex flex-wrap gap-1 mt-1.5">
                  <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium border ${companyInfo.typeColor}`}>
                    {companyInfo.typeLabel}
                  </span>
                  {companyInfo.categoryLabel && (
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium border bg-slate-100 text-slate-700 border-slate-300">
                      {companyInfo.categoryLabel}
                    </span>
                  )}
                </div>
              )}
              {/* Compliance context badge — shown when a compliance/cadence/recency filter is active */}
              {(complianceFilter.length > 0 || cadenceFilter.length > 0 || recencyFilter.length > 0) && (contact.days_since_reference != null || contact.days_since_last_contact != null) && (
                <div className="mt-1.5 text-[11px]" data-testid={`ctx-${contact.id}`}>
                  <span className="text-muted-foreground">
                    {contact.last_contact_at
                      ? `${contact.days_since_last_contact}d`
                      : (contact.days_since_reference != null ? <em>nunca · desde alta {contact.days_since_reference}d</em> : 'nunca')}
                  </span>
                  {contact.days_overdue > 0 && (
                    <span className="text-red-700 font-medium"> · +{contact.days_overdue}d de exceso</span>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="mt-3 space-y-1.5 overflow-hidden">
            {contact.email && (
              <div className="flex items-center gap-2 text-xs text-muted-foreground min-w-0">
                <Mail className="w-3.5 h-3.5 flex-shrink-0" />
                <span className="truncate block">{contact.email}</span>
              </div>
            )}
            {contact.phone && (
              <div className="flex items-center gap-2 text-xs text-muted-foreground min-w-0">
                <Phone className="w-3.5 h-3.5 flex-shrink-0" />
                <span className="truncate block">{contact.phone}</span>
              </div>
            )}
            <div className="flex items-center gap-2 text-xs text-muted-foreground min-w-0">
              <Building2 className="w-3.5 h-3.5 flex-shrink-0" />
              <span className="truncate block">{getCompanyName(contact.company_id)}</span>
            </div>
            {contact.owner && (
              <div className="flex items-center gap-2 text-xs text-muted-foreground min-w-0">
                <User className="w-3.5 h-3.5 flex-shrink-0" />
                <span className="truncate block">Resp: {contact.owner}</span>
              </div>
            )}
            {contact.linkedin && (
              <a 
                href={contact.linkedin.startsWith('http') ? contact.linkedin : `https://${contact.linkedin}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-xs text-blue-600 hover:text-blue-800 min-w-0"
                onClick={(e) => e.stopPropagation()}
              >
                <Linkedin className="w-3.5 h-3.5 flex-shrink-0" />
                <span className="truncate block">LinkedIn</span>
              </a>
            )}
            {/* Days since last contact/activity */}
            <div className="flex items-center gap-2 text-xs min-w-0" data-testid={`last-contact-${contact.id}`}>
              <Clock className="w-3.5 h-3.5 flex-shrink-0 text-slate-500" />
              <span className={`truncate ${
                contact.days_since_last_contact == null
                  ? 'text-muted-foreground'
                  : contact.days_since_last_contact >= 60
                  ? 'text-red-600 font-medium'
                  : contact.days_since_last_contact >= 30
                  ? 'text-orange-600'
                  : 'text-muted-foreground'
              }`}>
                {contact.days_since_last_contact == null
                  ? 'Sin contacto registrado'
                  : contact.days_since_last_contact === 0
                  ? 'Contactado hoy'
                  : `Hace ${contact.days_since_last_contact} día${contact.days_since_last_contact === 1 ? '' : 's'}`}
              </span>
            </div>
          </div>

          {/* Cultivation frequency + owner quick-select */}
          {isEditor && (
            <div
              className="mt-3 pt-3 border-t space-y-2"
              onClick={(e) => e.stopPropagation()}
              data-testid={`contact-frequency-block-${contact.id}`}
            >
              <div className="flex items-center gap-2">
                <Heart className="w-3.5 h-3.5 text-pink-500 flex-shrink-0" />
                <Select
                  value={contact.cultivation_frequency || '__none__'}
                  onValueChange={(val) => handleFrequencyChange(contact.id, val)}
                >
                  <SelectTrigger
                    className="h-8 text-xs"
                    data-testid={`contact-frequency-select-${contact.id}`}
                  >
                    <SelectValue placeholder="Sin configurar" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">Sin configurar</SelectItem>
                    {FREQUENCY_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-2">
                <User className="w-3.5 h-3.5 text-slate-500 flex-shrink-0" />
                <Select
                  value={contact.owner || '__none__'}
                  onValueChange={(val) => handleOwnerChange(contact.id, val)}
                >
                  <SelectTrigger
                    className="h-8 text-xs"
                    data-testid={`contact-owner-select-${contact.id}`}
                  >
                    <SelectValue placeholder="Sin responsable" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">Sin responsable</SelectItem>
                    {(editorUsers || []).map((u) => (
                      <SelectItem key={u.email} value={u.name}>
                        {u.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="p-4 md:p-8" data-testid="contacts-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl md:text-3xl font-semibold tracking-tight" data-testid="contacts-title">
            Contactos
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            {viewMode === 'analytics'
              ? <><span data-testid="contacts-header-count">{contacts.filter(c => matchesClientTypeFilter(getContactClientType(c), clientTypeFilter)).length}</span> contactos en el ámbito</>
              : <><span data-testid="contacts-header-count">{filteredContacts.length}</span> contactos{clientTypeFilter.length < 4 && <span className="text-xs ml-1">(filtrado)</span>}</>
            }
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex rounded-lg border p-1 bg-muted/30">
            <Button
              variant={viewMode === 'list' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('list')}
              className="h-8 px-3"
            >
              <List className="w-4 h-4 mr-2" />
              Lista
            </Button>
            <Button
              variant={viewMode === 'analytics' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('analytics')}
              className="h-8 px-3"
              data-testid="contacts-analytics-tab"
            >
              <BarChart3 className="w-4 h-4 mr-2" />
              Analíticas
            </Button>
          </div>
          <ClientTypeFilter 
            selectedTypes={clientTypeFilter}
            onToggle={toggleClientTypeFilter}
          />
          <ExportMenu endpoint="contacts" />
          {isEditor && (
            <Button onClick={() => { setEditingContact(null); setDialogOpen(true); }} data-testid="add-contact-btn">
              <Plus className="w-4 h-4 mr-2" />
              Nuevo Contacto
            </Button>
          )}
        </div>
      </div>

      {viewMode === 'analytics' ? (
        <ContactsAnalytics clientTypeFilter={clientTypeFilter} />
      ) : (
        <>
          {/* Search and Sort */}
          <div className="flex flex-col sm:flex-row gap-3 mb-6">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                <Input
                  placeholder="Buscar contactos..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10"
                  data-testid="contacts-search"
                />
              </div>
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="w-[200px]" data-testid="sort-select">
                  <ArrowUpDown className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Ordenar por" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="last_name">Alfabético (Apellido)</SelectItem>
                  <SelectItem value="owner">Por Responsable</SelectItem>
                  <SelectItem value="company_id">Por Empresa</SelectItem>
                </SelectContent>
              </Select>
          </div>

          {/* Advanced filter selectors (Popover-based multi-select) */}
          <div className="flex flex-wrap items-center gap-2 mb-2" data-testid="adv-filter-selectors">
            {[
              ['compliance', 'Cumplimiento', ['al_dia','fuera_de_plazo','sin_fecha','excluido','sin_compromiso'], complianceFilter, setComplianceFilter],
              ['cadence', 'Cadencia', ['estrategico','prioritario','activo','orbita','no_seguir','sin_definir'], cadenceFilter, setCadenceFilter],
              ['recency', 'Antigüedad', ['0-30','31-90','91-180','181-365','gt-365','nunca'], recencyFilter, setRecencyFilter],
              ['seniority', 'Nivel', ['direccion','mando_intermedio','tecnico','otros','sin_cargo'], seniorityFilter, setSeniorityFilter],
            ].map(([kind, label, opts, val, setter]) => (
              <DropdownMenu key={kind}>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" className="h-8 text-xs" data-testid={`filter-${kind}-btn`}>
                    <Filter className="w-3 h-3 mr-1" />
                    {label}{val.length > 0 ? ` (${val.length})` : ''}
                    <ChevronDown className="w-3 h-3 ml-1" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-56">
                  {opts.map(o => {
                    const checked = val.includes(o);
                    return (
                      <DropdownMenuItem
                        key={o}
                        onSelect={(e) => { e.preventDefault(); toggleInSet(setter, val, o); }}
                        data-testid={`filter-${kind}-opt-${o}`}
                        className="cursor-pointer text-xs"
                      >
                        <span className={`inline-block w-3 h-3 mr-2 border rounded ${checked ? 'bg-primary border-primary' : 'border-muted-foreground'}`}>
                          {checked && <span className="text-white text-[8px] leading-3 pl-[2px]">✓</span>}
                        </span>
                        {humanLabel(kind, o)}
                      </DropdownMenuItem>
                    );
                  })}
                </DropdownMenuContent>
              </DropdownMenu>
            ))}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="h-8 text-xs" data-testid="filter-owner-btn">
                  <Filter className="w-3 h-3 mr-1" />
                  Responsable{ownerAdvFilter.length > 0 ? ` (${ownerAdvFilter.length})` : ''}
                  <ChevronDown className="w-3 h-3 ml-1" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-64 max-h-72 overflow-y-auto">
                {[{ name: 'Sin asignar', __key: '__unassigned__' }, ...(editorUsers || []).map(u => ({ name: u.name, __key: u.name }))].map(o => {
                  const checked = ownerAdvFilter.includes(o.__key);
                  return (
                    <DropdownMenuItem
                      key={o.__key}
                      onSelect={(e) => { e.preventDefault(); toggleInSet(setOwnerAdvFilter, ownerAdvFilter, o.__key); }}
                      data-testid={`filter-owner-opt-${o.__key}`}
                      className="cursor-pointer text-xs"
                    >
                      <span className={`inline-block w-3 h-3 mr-2 border rounded ${checked ? 'bg-primary border-primary' : 'border-muted-foreground'}`}>
                        {checked && <span className="text-white text-[8px] leading-3 pl-[2px]">✓</span>}
                      </span>
                      {o.name}
                    </DropdownMenuItem>
                  );
                })}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {/* Advanced filter chips + count */}
          <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="adv-filter-bar">
            <div className="text-sm font-medium text-muted-foreground">
              <span data-testid="contacts-count">{filteredContacts.length} de {contacts.length}</span> contactos
            </div>
            {anyAdvFilterActive && (
              <>
                <span className="text-muted-foreground text-xs">·</span>
                {complianceFilter.map(v => (
                  <button key={`c-${v}`} type="button" onClick={() => setComplianceFilter(complianceFilter.filter(x => x !== v))} className="inline-flex items-center gap-1 text-xs bg-primary/10 text-primary rounded-full px-2 py-0.5 hover:bg-primary/20" data-testid={`chip-compliance-${v}`}>
                    Estado: {humanLabel('compliance', v)} <X className="w-3 h-3" />
                  </button>
                ))}
                {cadenceFilter.map(v => (
                  <button key={`d-${v}`} type="button" onClick={() => setCadenceFilter(cadenceFilter.filter(x => x !== v))} className="inline-flex items-center gap-1 text-xs bg-primary/10 text-primary rounded-full px-2 py-0.5 hover:bg-primary/20" data-testid={`chip-cadence-${v}`}>
                    Cadencia: {humanLabel('cadence', v)} <X className="w-3 h-3" />
                  </button>
                ))}
                {recencyFilter.map(v => (
                  <button key={`r-${v}`} type="button" onClick={() => setRecencyFilter(recencyFilter.filter(x => x !== v))} className="inline-flex items-center gap-1 text-xs bg-primary/10 text-primary rounded-full px-2 py-0.5 hover:bg-primary/20" data-testid={`chip-recency-${v}`}>
                    Antigüedad: {humanLabel('recency', v)} <X className="w-3 h-3" />
                  </button>
                ))}
                {seniorityFilter.map(v => (
                  <button key={`s-${v}`} type="button" onClick={() => setSeniorityFilter(seniorityFilter.filter(x => x !== v))} className="inline-flex items-center gap-1 text-xs bg-primary/10 text-primary rounded-full px-2 py-0.5 hover:bg-primary/20" data-testid={`chip-seniority-${v}`}>
                    Nivel: {humanLabel('seniority', v)} <X className="w-3 h-3" />
                  </button>
                ))}
                {ownerAdvFilter.map(v => (
                  <button key={`o-${v}`} type="button" onClick={() => setOwnerAdvFilter(ownerAdvFilter.filter(x => x !== v))} className="inline-flex items-center gap-1 text-xs bg-primary/10 text-primary rounded-full px-2 py-0.5 hover:bg-primary/20" data-testid={`chip-owner-${v}`}>
                    Responsable: {v === '__unassigned__' ? 'Sin asignar' : v} <X className="w-3 h-3" />
                  </button>
                ))}
                <Button size="sm" variant="ghost" onClick={clearAllAdvFilters} className="h-6 px-2 text-xs" data-testid="clear-filters-btn">
                  Limpiar filtros
                </Button>
              </>
            )}
          </div>

          {loading ? (
        <div className="text-sm text-muted-foreground">Cargando...</div>
      ) : filteredContacts.length === 0 ? (
        <div className="text-center py-12" data-testid="contacts-empty-state">
          <User className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
          <div className="text-muted-foreground mb-4">
            {anyAdvFilterActive
              ? 'No hay contactos que cumplan los filtros activos'
              : (search ? 'No se encontraron contactos' : 'No hay contactos')}
          </div>
          {anyAdvFilterActive && (
            <Button variant="outline" onClick={clearAllAdvFilters} className="mr-2" data-testid="empty-clear-filters">
              <X className="w-4 h-4 mr-2" /> Quitar filtros
            </Button>
          )}
          {!search && !anyAdvFilterActive && (
            <Button onClick={() => setDialogOpen(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Crear primer contacto
            </Button>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {groupedContacts.map((group) => {
            // All sort modes support collapsible groups (alphabetical, owner, company)
            const isCollapsible = true;
            // Default: alphabetical starts EXPANDED (letters are just headers);
            // owner/company start COLLAPSED (see summary first). User toggle overrides either way.
            const defaultExpanded = sortBy === 'last_name' || anyAdvFilterActive;
            const userToggled = Object.prototype.hasOwnProperty.call(expandedGroups, group.key);
            const isExpanded = userToggled ? expandedGroups[group.key] : defaultExpanded;
            
            // Get total contacts including subgroups for TYPSA
            const totalContacts = group.isTypsa 
              ? group.contacts.length + Object.values(group.subgroups || {}).reduce((sum, sg) => sum + sg.contacts.length, 0)
              : group.contacts.length;

            // Sort subgroups by category order then value
            const sortedSubgroups = group.isTypsa && group.subgroups
              ? Object.entries(group.subgroups)
                  .sort(([, a], [, b]) => {
                    const catOrder = ['division', 'departamento', 'territorio', 'grupo'];
                    const catA = catOrder.indexOf(a.category);
                    const catB = catOrder.indexOf(b.category);
                    if (catA !== catB) return catA - catB;
                    return a.value.localeCompare(b.value, 'es');
                  })
              : [];
            
            // Breakdown for owner sorting
            const breakdown = group.breakdown;
            const hasBreakdown = sortBy === 'owner' && breakdown;
            
            return (
              <div key={group.key}>
                {/* Group Header */}
                {group.label && (
                  <div 
                    className={`py-3 px-4 border rounded-lg bg-muted/50 ${isCollapsible ? 'cursor-pointer hover:bg-muted transition-colors' : ''}`}
                    onClick={() => isCollapsible && toggleGroup(group.key)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        {isCollapsible && (
                          isExpanded 
                            ? <ChevronDown className="w-5 h-5 text-muted-foreground" />
                            : <ChevronRight className="w-5 h-5 text-muted-foreground" />
                        )}
                        <div className="flex-1">
                          <div className="flex items-center gap-3">
                            <h2 className="text-lg font-semibold text-primary">{group.label}</h2>
                            <span className="text-sm text-muted-foreground">
                              {totalContacts} contacto{totalContacts !== 1 ? 's' : ''}
                            </span>
                          </div>
                          {/* Breakdown by company type */}
                          {hasBreakdown && (
                            <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1.5 text-xs text-muted-foreground">
                              {breakdown.typsa.total > 0 && (
                                <span className="flex items-center gap-1">
                                  <span className="w-2 h-2 rounded-full bg-blue-500"></span>
                                  TYPSA: {breakdown.typsa.total}
                                  {(breakdown.typsa.division > 0 || breakdown.typsa.departamento > 0 || breakdown.typsa.territorio > 0 || breakdown.typsa.grupo > 0) && (
                                    <span className="text-muted-foreground/70 ml-1">
                                      ({breakdown.typsa.division > 0 && `Div: ${breakdown.typsa.division}`}
                                      {breakdown.typsa.division > 0 && (breakdown.typsa.departamento > 0 || breakdown.typsa.territorio > 0 || breakdown.typsa.grupo > 0) && ', '}
                                      {breakdown.typsa.departamento > 0 && `Dep: ${breakdown.typsa.departamento}`}
                                      {breakdown.typsa.departamento > 0 && (breakdown.typsa.territorio > 0 || breakdown.typsa.grupo > 0) && ', '}
                                      {breakdown.typsa.territorio > 0 && `Ter: ${breakdown.typsa.territorio}`}
                                      {breakdown.typsa.territorio > 0 && breakdown.typsa.grupo > 0 && ', '}
                                      {breakdown.typsa.grupo > 0 && `Gru: ${breakdown.typsa.grupo}`})
                                    </span>
                                  )}
                                </span>
                              )}
                              {breakdown.publica > 0 && (
                                <span className="flex items-center gap-1">
                                  <span className="w-2 h-2 rounded-full bg-green-500"></span>
                                  Públicas: {breakdown.publica}
                                </span>
                              )}
                              {breakdown.privada > 0 && (
                                <span className="flex items-center gap-1">
                                  <span className="w-2 h-2 rounded-full bg-purple-500"></span>
                                  Privadas: {breakdown.privada}
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Contact Cards - only show if expanded */}
                {isExpanded && (
                  <div className={`${group.label ? 'mt-3' : ''}`}>
                    {/* TYPSA Subgroups */}
                    {group.isTypsa && sortedSubgroups.length > 0 && (
                      <div className="space-y-3 mb-4">
                        {sortedSubgroups.map(([subKey, subgroup]) => {
                          const subgroupKey = `${group.key}-${subKey}`;
                          const isSubExpanded = expandedGroups[subgroupKey] ?? false;
                          
                          return (
                            <div key={subKey} className="ml-4">
                              {/* Subgroup Header */}
                              <div 
                                className="py-2 px-3 border rounded-lg bg-blue-50/50 dark:bg-blue-900/10 cursor-pointer hover:bg-blue-100/50 dark:hover:bg-blue-900/20 transition-colors"
                                onClick={() => toggleGroup(subgroupKey)}
                              >
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-2">
                                    {isSubExpanded 
                                      ? <ChevronDown className="w-4 h-4 text-blue-600" />
                                      : <ChevronRight className="w-4 h-4 text-blue-600" />
                                    }
                                    <span className="text-sm font-medium text-blue-700 dark:text-blue-400">
                                      {getTypsaCategoryLabel(subgroup.category)}: {subgroup.value}
                                    </span>
                                  </div>
                                  <span className="text-xs text-blue-600">{subgroup.contacts.length}</span>
                                </div>
                              </div>
                              
                              {/* Subgroup Contacts */}
                              {isSubExpanded && (
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-2 ml-2">
                                  {subgroup.contacts.map((contact) => (
                                    <ContactCard key={contact.id} contact={contact} />
                                  ))}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                    
                    {/* Ungrouped contacts (or non-TYPSA contacts) */}
                    {group.contacts.length > 0 && (
                      <div>
                        {group.isTypsa && sortedSubgroups.length > 0 && (
                          <div className="ml-4 mb-2">
                            <span className="text-sm text-muted-foreground">Sin clasificar ({group.contacts.length})</span>
                          </div>
                        )}
                        <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 ${group.isTypsa && sortedSubgroups.length > 0 ? 'ml-4' : ''}`}>
                          {group.contacts.map((contact) => (
                            <ContactCard key={contact.id} contact={contact} />
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      </>
      )}

      {/* Contact Dialog for Create/Edit */}
      <ContactDialog 
        open={dialogOpen} 
        onOpenChange={handleDialogClose}
        onSuccess={handleDialogSuccess}
        contact={editingContact}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteConfirm} onOpenChange={(open) => !open && setDeleteConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar Contacto</AlertDialogTitle>
            <AlertDialogDescription>
              ¿Estás seguro de que quieres eliminar a "{deleteConfirm ? getFullName(deleteConfirm) : ''}"? Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-contact-btn">Cancelar</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={deleting}
              data-testid="confirm-delete-contact-btn"
            >
              {deleting ? 'Eliminando...' : 'Eliminar'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Contacts;
